import { ExternalLink, Flag, Star } from "lucide-react";
import ChapterHeading from "@/components/ChapterHeading";
import Reveal from "@/components/Reveal";
import CTABand from "@/components/CTABand";
import { Stars } from "@/pages/Home";
import usePageMeta from "@/hooks/usePageMeta";
import useReviews from "@/hooks/useReviews";
import { useContent } from "@/content/ContentContext";
import { GOOGLE_PROFILE_URL, GOOGLE_REVIEW_URL, GOOGLE_RATING, MANUAL_REVIEWS } from "@/data/reviews";

export default function Reviews() {
  const { get } = useContent();
  const data = useReviews();
  usePageMeta(
    get("meta_reviews_title", "Reviews | 5.0-Star Google Rating | Modify Perth"),
    get("meta_reviews_desc", "Perth homeowners rate Modify 5.0 on Google for grab rail, hand rail and rubber ramp installations. Read genuine reviews from real local customers.")
  );

  const live = data?.configured && data?.rating;

  return (
    <div data-testid="reviews-page">
      <section className="bg-ivory">
        <div className="mx-auto max-w-7xl px-4 pb-16 pt-44 sm:px-6 lg:px-8 lg:pb-24 lg:pt-40">
          <ChapterHeading
            num="01"
            eyebrow="What our customers say"
            title="5-STAR SERVICE. REAL CUSTOMER EXPERIENCES."
            testid="reviews-heading"
          />

          {live ? (
            <div className="mt-12">
              <Reveal>
                <div className="max-w-3xl rounded-3xl border border-line bg-white p-8 sm:p-10" data-testid="reviews-summary">
                  <div className="flex flex-wrap items-center gap-4">
                    <span className="font-display text-5xl font-extrabold text-charcoal">{data.rating}</span>
                    <div>
                      <Stars size="h-6 w-6" count={Math.round(data.rating)} />
                      <p className="mt-2 text-sm font-semibold text-ink/60">
                        Genuine Google reviews from Perth customers
                      </p>
                    </div>
                  </div>
                  {data.google_maps_uri && (
                    <a
                      href={data.google_maps_uri}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="reviews-google-maps-link"
                      className="mt-5 inline-flex min-h-[44px] items-center gap-2 rounded-full border-2 border-charcoal px-5 py-2 text-xs font-bold tracking-[0.1em] text-charcoal transition-colors duration-300 hover:bg-charcoal hover:text-white"
                    >
                      SEE ALL REVIEWS ON GOOGLE MAPS
                      <ExternalLink className="h-4 w-4" aria-hidden="true" />
                    </a>
                  )}
                  <a
                    href={GOOGLE_REVIEW_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="leave-review-button-live"
                    className="mt-5 inline-flex min-h-[44px] items-center gap-2 rounded-full bg-ember px-5 py-2 text-xs font-bold tracking-[0.1em] text-charcoal transition-colors duration-300 hover:bg-ember-dark hover:text-white"
                  >
                    <Star className="h-4 w-4 fill-gold text-gold" aria-hidden="true" />
                    LEAVE A REVIEW
                  </a>
                </div>
              </Reveal>

              <div className="mt-8 grid gap-5 md:grid-cols-2">
                {data.reviews.map((review, i) => (
                  <Reveal key={`${review.google_maps_uri || "review"}-${i}`} delay={Math.min(i * 0.06, 0.3)}>
                    <article data-testid={`review-card-${i}`} className="flex h-full flex-col rounded-3xl border border-line bg-white p-7">
                      <Stars size="h-4 w-4" count={Math.round(review.rating || 5)} />
                      <p className="mt-4 flex-1 text-base leading-relaxed text-ink/80">
                        {review.text || "(No written review)"}
                      </p>
                      <div className="mt-5 flex flex-wrap items-center justify-between gap-3 border-t border-line pt-4">
                        <div>
                          {review.author_uri ? (
                            <a
                              href={review.author_uri}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="font-display text-sm font-bold text-charcoal underline decoration-sage decoration-2 underline-offset-4"
                            >
                              {review.author_name || "Google user"}
                            </a>
                          ) : (
                            <span className="font-display text-sm font-bold text-charcoal">
                              {review.author_name || "Google user"}
                            </span>
                          )}
                          {review.relative_time_description && (
                            <p className="mt-1 text-xs text-ink/50">{review.relative_time_description}</p>
                          )}
                        </div>
                        <div className="flex items-center gap-3 text-xs font-semibold">
                          {review.google_maps_uri && (
                            <a
                              href={review.google_maps_uri}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1 text-sage-dark hover:text-charcoal"
                            >
                              Google Maps
                              <ExternalLink className="h-3 w-3" aria-hidden="true" />
                            </a>
                          )}
                          {review.flag_content_uri && (
                            <a
                              href={review.flag_content_uri}
                              target="_blank"
                              rel="noopener noreferrer"
                              aria-label="Report this review to Google"
                              className="inline-flex items-center gap-1 text-ink/40 hover:text-charcoal"
                            >
                              <Flag className="h-3 w-3" aria-hidden="true" />
                              Report
                            </a>
                          )}
                        </div>
                      </div>
                    </article>
                  </Reveal>
                ))}
              </div>
              <p className="mt-6 text-xs text-ink/50">
                Reviews are shown in relevance order, as provided by Google. Google reviews are not verified by this
                website.
              </p>
            </div>
          ) : (
            <div className="mt-12">
              <Reveal>
                <div className="max-w-3xl rounded-3xl border border-line bg-white p-8 sm:p-10" data-testid="reviews-summary">
                  <div className="flex flex-wrap items-center gap-4">
                    <span className="font-display text-5xl font-extrabold text-charcoal">{GOOGLE_RATING.toFixed(1)}</span>
                    <div>
                      <Stars size="h-6 w-6" count={5} />
                      <p className="mt-2 text-sm font-semibold text-ink/60">
                        Genuine Google reviews from Perth customers
                      </p>
                    </div>
                  </div>
                  <div className="mt-5 flex flex-wrap gap-3">
                    <a
                      href={GOOGLE_REVIEW_URL}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="leave-review-button"
                      className="inline-flex min-h-[44px] items-center gap-2 rounded-full bg-ember px-5 py-2 text-xs font-bold tracking-[0.1em] text-charcoal transition-colors duration-300 hover:bg-ember-dark hover:text-white"
                    >
                      <Star className="h-4 w-4 fill-gold text-gold" aria-hidden="true" />
                      LEAVE A REVIEW
                    </a>
                    <a
                      href={GOOGLE_PROFILE_URL}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="reviews-google-profile-link"
                      className="inline-flex min-h-[44px] items-center gap-2 rounded-full border-2 border-charcoal px-5 py-2 text-xs font-bold tracking-[0.1em] text-charcoal transition-colors duration-300 hover:bg-charcoal hover:text-white"
                    >
                      SEE ALL REVIEWS ON GOOGLE
                      <ExternalLink className="h-4 w-4" aria-hidden="true" />
                    </a>
                  </div>
                </div>
              </Reveal>

              <div className="mt-8 grid gap-5 md:grid-cols-2">
                {MANUAL_REVIEWS.map((review, i) => (
                  <Reveal key={review.author} delay={Math.min(i * 0.06, 0.3)}>
                    <article data-testid={`review-card-${i}`} className="flex h-full flex-col rounded-3xl border border-line bg-white p-7">
                      <Stars size="h-4 w-4" count={review.rating} />
                      <p className="mt-4 flex-1 text-base leading-relaxed text-ink/80">{review.text}</p>
                      <div className="mt-5 flex flex-wrap items-center justify-between gap-3 border-t border-line pt-4">
                        <div>
                          <span className="font-display text-sm font-bold text-charcoal">{review.author}</span>
                          <p className="mt-1 text-xs text-ink/50">{review.time}</p>
                        </div>
                        <span className="text-xs font-semibold text-ink/40">Google review</span>
                      </div>
                    </article>
                  </Reveal>
                ))}
              </div>
              <p className="mt-6 text-xs text-ink/50">
                These are genuine reviews from our Google Business Profile. Live review syncing is coming soon.
              </p>

              <Reveal delay={0.1}>
                <div className="mt-8 flex max-w-3xl flex-col items-start gap-5 rounded-3xl border border-line bg-sage-light p-8 sm:flex-row sm:items-center sm:justify-between" data-testid="leave-review-card">
                  <div>
                    <h2 className="font-display text-xl font-bold text-charcoal sm:text-2xl">
                      Had Modify work on your home?
                    </h2>
                    <p className="mt-2 max-w-md text-sm leading-relaxed text-ink/70">
                      Your review helps other Perth families find a team they can trust. It takes less than a minute.
                    </p>
                  </div>
                  <a
                    href={GOOGLE_REVIEW_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="leave-review-button-bottom"
                    className="inline-flex min-h-[48px] shrink-0 items-center gap-2 rounded-full bg-ember px-7 py-3 text-sm font-bold tracking-[0.08em] text-charcoal transition-colors duration-300 hover:bg-ember-dark hover:text-white"
                  >
                    <Star className="h-4 w-4 fill-gold text-gold" aria-hidden="true" />
                    LEAVE A REVIEW
                  </a>
                </div>
              </Reveal>
            </div>
          )}
        </div>
      </section>

      <CTABand
        title="READY TO EXPERIENCE IT YOURSELF?"
        copy="Get in touch about grab rails, hand rails or rubber ramps for your home — supplied and professionally installed across Perth."
        testid="reviews-cta"
      />
    </div>
  );
}
